<template>
  <transition name="mobile-sheet-fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[1200] bg-black/30 lg:hidden"
      @click.self="close"
    >
      <transition name="mobile-sheet-slide">
        <section
          v-if="modelValue"
          class="absolute inset-x-0 bottom-0 max-h-[88dvh] overflow-y-auto rounded-t-[30px] bg-white px-5 pb-[calc(var(--layout-mobile-nav-height)+28px)] pt-5 shadow-[0_-12px_32px_rgb(0_0_0_/_0.12)]"
        >
          <div
            class="mx-auto flex w-full max-w-[var(--layout-mobile-max-width)] flex-col gap-5"
          >
            <div class="flex items-center justify-between gap-4">
              <p
                class="text-[28px] leading-[1.05] font-helvetica-700 text-gray-dark"
              >
                Меню
              </p>

              <button
                type="button"
                class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-border bg-white-gray text-gray-dark"
                aria-label="Закрыть меню"
                @click="close"
              >
                <span
                  class="absolute h-[1.5px] w-4 rotate-45 rounded-full bg-current"
                ></span>
                <span
                  class="absolute h-[1.5px] w-4 -rotate-45 rounded-full bg-current"
                ></span>
              </button>
            </div>

            <div
              class="rounded-[24px] border border-gray-border bg-white-gray px-4 py-4"
            >
              <div class="flex items-start gap-3">
                <div
                  class="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white text-[18px] font-helvetica-700 text-orange"
                >
                  <img
                    v-if="managerPhotoUrl"
                    :src="managerPhotoUrl"
                    alt="Фото менеджера"
                    class="h-full w-full object-cover"
                  />
                  <span v-else>
                    {{ managerInitial }}
                  </span>
                </div>

                <div class="min-w-0 flex-1">
                  <p
                    class="mb-1 text-[12px] font-helvetica-700 uppercase tracking-[0.06em] text-orange"
                  >
                    Ваш менеджер
                  </p>
                  <p
                    class="text-[16px] leading-[1.3] font-helvetica-700 text-gray-dark"
                  >
                    {{ managerName }}
                  </p>
                  <a
                    v-if="managerPhone"
                    :href="`tel:${managerPhone}`"
                    class="mt-2 block text-[14px] text-gray-dark"
                  >
                    {{ managerPhone }}
                  </a>
                  <a
                    v-if="managerEmail"
                    :href="`mailto:${managerEmail}`"
                    class="mt-1 block break-all text-[14px] text-extra-text-color"
                  >
                    {{ managerEmail }}
                  </a>
                </div>
              </div>
            </div>

            <div
              v-if="canViewGoods"
              class="grid grid-cols-1 gap-2 min-[390px]:grid-cols-2"
            >
              <NuxtLink
                to="/favorites"
                class="flex min-w-0 items-center gap-3 rounded-[18px] border border-gray-border bg-white px-4 py-3 text-gray-dark transition-colors active:border-orange"
                @click="close"
              >
                <span
                  class="mobile-menu-shortcut-icon mobile-menu-shortcut-icon--star"
                >
                  <SvgoIconComponentHIconStar class="h-[22px] w-[22px]"></SvgoIconComponentHIconStar>
                </span>
                <span
                  class="min-w-0 text-[15px] leading-[1.2] font-helvetica-700"
                >
                  Избранное
                </span>
              </NuxtLink>

              <NuxtLink
                to="/compare"
                class="flex min-w-0 items-center gap-3 rounded-[18px] border border-gray-border bg-white px-4 py-3 text-gray-dark transition-colors active:border-orange"
                @click="close"
              >
                <span
                  class="mobile-menu-shortcut-icon mobile-menu-shortcut-icon--compare"
                >
                  <SvgoIconComponentHIconCompare class="h-[22px] w-[22px]"></SvgoIconComponentHIconCompare>
                  <span
                    v-if="compareCount > 0"
                    class="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-white bg-orange px-1 text-[10px] font-helvetica-700 leading-none text-white"
                  >
                    {{ compareCount }}
                  </span>
                </span>
                <span
                  class="min-w-0 text-[15px] leading-[1.2] font-helvetica-700"
                >
                  Сравнение
                </span>
              </NuxtLink>
            </div>

            <div class="flex flex-col gap-2">
              <NuxtLink
                v-for="item in menuItems"
                :key="item.to"
                :to="item.to"
                class="flex items-center justify-between rounded-[20px] border border-gray-border bg-white px-4 py-4 text-gray-dark transition-colors active:border-orange"
                @click="close"
              >
                <div class="flex items-center gap-3">
                  <component :is="item.icon" class="h-5 w-5 text-gray-dark" />
                  <span class="text-[16px] font-helvetica-700">
                    {{ item.label }}
                  </span>
                </div>
                <span class="text-[20px] leading-none text-extra-text-color">
                  →
                </span>
              </NuxtLink>
            </div>
          </div>
        </section>
      </transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useCompare } from "~/composables/useCompare";
import { useLoginResponse } from "~/composables/useLoginResponse";
import DeliveryIcon from "~/assets/svg/menu/delivery.svg?component";
import FinanceIcon from "~/assets/svg/menu/finance.svg?component";
import GoodsIcon from "~/assets/svg/menu/goods.svg?component";
import HomeIcon from "~/assets/svg/menu/home.svg?component";
import MobileChatIcon from "~/components/mobile/MobileChatIcon.vue";
import OrderIcon from "~/assets/svg/menu/order.svg?component";
import SettingsIcon from "~/assets/svg/menu/settings.svg?component";

type MobileMenuItem = {
  label: string;
  to: string;
  icon: any;
};

type LoginResponse = {
  Менеджер?: string;
  ТелефонМенеджера?: string;
  EMailМенеджера?: string;
  ФотоМенеджера?: string;
  АссистентМенеджера?: string;
  ТелефонАссистентаМенеджера?: string;
  EMailАссистентаМенеджера?: string;
  ФотоАссистентаМенеджера?: string;
};

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const normalizeText = (value: unknown) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const parseLoginResponse = (raw: string | null): LoginResponse | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") {
      return JSON.parse(parsed) as LoginResponse;
    }

    return parsed as LoginResponse;
  } catch {
    return null;
  }
};

const managerInfo = ref<LoginResponse | null>(null);
const { count: compareCount } = useCompare();
const { hydrateLoginResponse, canViewGoods } = useLoginResponse();

const managerName = computed(() => {
  const source = managerInfo.value;
  if (!source) return "Менеджер";

  return (
    normalizeText(source.Менеджер) ||
    normalizeText(source.АссистентМенеджера) ||
    "Менеджер"
  );
});

const managerPhone = computed(() => {
  const source = managerInfo.value;
  if (!source) return "";

  return (
    normalizeText(source.ТелефонМенеджера) ||
    normalizeText(source.ТелефонАссистентаМенеджера)
  );
});

const managerEmail = computed(() => {
  const source = managerInfo.value;
  if (!source) return "";

  return (
    normalizeText(source.EMailМенеджера) ||
    normalizeText(source.EMailАссистентаМенеджера)
  );
});

const managerPhotoUrl = computed(() => {
  const source = managerInfo.value;
  if (!source) return "";

  return (
    normalizeText(source.ФотоМенеджера) ||
    normalizeText(source.ФотоАссистентаМенеджера)
  );
});

const managerInitial = computed(() =>
  managerName.value.charAt(0).toUpperCase(),
);

const menuItems: MobileMenuItem[] = [
  { label: "Главная", to: "/", icon: HomeIcon },
  { label: "Каталог", to: "/search", icon: GoodsIcon },
  { label: "Заказы", to: "/orders", icon: OrderIcon },
  { label: "Финансы", to: "/finance", icon: FinanceIcon },
  { label: "Чат с менеджером", to: "/chat", icon: MobileChatIcon },
  { label: "Отгрузки", to: "/delivery", icon: DeliveryIcon },
  { label: "Личный кабинет", to: "/profile", icon: SettingsIcon },
];

const close = () => emit("update:modelValue", false);

watch(
  () => props.modelValue,
  (value) => {
    if (!value || process.server) return;
    hydrateLoginResponse();
    managerInfo.value = parseLoginResponse(
      localStorage.getItem("login_response"),
    );
  },
  { immediate: true },
);
</script>

<style scoped>
.mobile-menu-shortcut-icon {
  position: relative;
  display: grid;
  height: 40px;
  width: 40px;
  flex: none;
  place-items: center;
  overflow: visible;
  border-radius: 10px;
  border: 1px solid var(--color-gray-border);
  background: var(--color-white-gray);
}

.mobile-menu-shortcut-icon :deep(svg) {
  display: block;
}

.mobile-menu-shortcut-icon--star :deep(svg) {
  transform: translate(0.5px, 0.5px);
}

.mobile-menu-shortcut-icon--compare :deep(svg) {
  transform: translate(0.25px, 0.5px);
}
</style>

<style scoped>
.mobile-sheet-fade-enter-active,
.mobile-sheet-fade-leave-active,
.mobile-sheet-slide-enter-active,
.mobile-sheet-slide-leave-active {
  transition: all 0.2s ease;
}

.mobile-sheet-fade-enter-from,
.mobile-sheet-fade-leave-to {
  opacity: 0;
}

.mobile-sheet-slide-enter-from,
.mobile-sheet-slide-leave-to {
  transform: translateY(18px);
  opacity: 0;
}
</style>
